import { useState, useEffect, useCallback } from "react";
import { SidebarNav } from "./SidebarNav";
import { Topbar } from "./Topbar";
import { AppLauncher } from "./AppLauncher";
import { readBootstrap, subscribeBootstrap, type UiBootstrap } from "../../lib/bootstrap";
import { initWebMode } from "../../lib/web-ipc";

interface AppShellProps {
  children: (route: string, payload: unknown) => React.ReactNode;
}

const ROUTE_LABELS: Record<string, string> = {
  home: "Home",
  projects: "Projects",
  chat: "Chat",
  browser: "Browser",
  canvas: "Canvas Studio",
  taskboard: "Task Board",
  tools: "Tools",
  files: "Files",
  settings: "Settings",
  cost: "Cost Ledger",
  memory: "Memory",
  quarantine: "Quarantine",
  recipes: "Recipes",
  archmap: "Architecture Map",
  benchmark: "Benchmark",
  drift: "Drift Detector",
  provenance: "Provenance",
  replay: "Replay",
  registry: "Registry Admin",
  access: "Access Control",
  admin: "Admin Console",
};

export function AppShell({ children }: AppShellProps) {
  const [bootstrap, setBootstrap] = useState<UiBootstrap>(readBootstrap);
  const [webMode, setWebMode] = useState(false);
  const [provider, setProvider] = useState("detecting...");
  const [appsOpen, setAppsOpen] = useState(false);

  // Initialize web mode on mount
  useEffect(() => {
    initWebMode().then((isWeb) => {
      setWebMode(isWeb);
      if (isWeb) {
        fetch("/api/status")
          .then((r) => r.json())
          .then((d) => setProvider(d.active_provider || "unknown"))
          .catch(() => {});
      }
    });
  }, []);

  // Listen for backend-driven navigation
  useEffect(() => {
    return subscribeBootstrap((b) => setBootstrap(b));
  }, []);

  // Listen for SSE provider changes
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.provider) setProvider(detail.provider);
    };
    window.addEventListener("opentool-ipc", handler);
    return () => window.removeEventListener("opentool-ipc", handler);
  }, []);

  const handleNavigate = useCallback(
    (route: string) => {
      setBootstrap((prev) => ({ ...prev, route: route as UiBootstrap["route"] }));
      window.location.hash = route;
    },
    [],
  );

  // Support URL hash routing (e.g. #chat, #files, #projects)
  useEffect(() => {
    const hash = window.location.hash.slice(1);
    if (hash && hash !== bootstrap.route) {
      setBootstrap((prev) => ({ ...prev, route: hash as UiBootstrap["route"] }));
    }
    const onHash = () => {
      const h = window.location.hash.slice(1);
      if (h) setBootstrap((prev) => ({ ...prev, route: h as UiBootstrap["route"] }));
    };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  const route = bootstrap.route || "home";
  const projectName = bootstrap.projectName || "workspace";

  const toggleApps = useCallback(() => {
    setAppsOpen((prev) => !prev);
  }, []);

  const closeApps = useCallback(() => {
    setAppsOpen(false);
  }, []);

  return (
    <div className="shell-app">
      <SidebarNav
        activeRoute={route}
        onNavigate={handleNavigate}
        appName={projectName}
        onOpenApps={toggleApps}
        appsOpen={appsOpen}
      />
      <div className="shell-main">
        <Topbar
          appName={projectName}
          routeLabel={ROUTE_LABELS[route] || route}
          breadcrumb={`workspace / ${route}`}
          provider={provider}
          status="ready"
        />
        <div className="shell-content">
          {children(route, bootstrap.payload)}
        </div>
      </div>

      {/* App Launcher overlay */}
      {appsOpen && (
        <AppLauncher
          onNavigate={handleNavigate}
          onClose={closeApps}
          activeRoute={route}
        />
      )}
    </div>
  );
}
